import { GeneralModalProps, Point, RootState } from "../../assets/interfaces/interfaces";
import { Modal, IconButton, Box, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckFill from "../../assets/images/finchatGPT/Check_fill.png";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import Alert from "../../assets/images/finchatGPT/alert.png";
import moment from "moment";
import useEnhancedNavigate from "../Session/helpers";

const PlanModal: React.FC<GeneralModalProps> = ({ open, handleClose }) => {
  const isDarkMode = useSelector((state: any) => state.theme.darkMode);
  // Define the text color based on the theme
  const textColorClass = isDarkMode ? "text-white" : "text-black";
  const navigate = useEnhancedNavigate();
  const style = {
    position: "absolute" as "absolute",
    top: "45%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    maxWidth: '417px'
  };

  const points: Point[] = [
    { id: 1, text: "Charts" },
    { id: 2, text: "Stock Screener on 10,000 Stocks" },
    { id: 3, text: "Top Gainers and Losers" },
    { id: 4, text: "Company Financials Data" },
    { id: 5, text: "Options Data" },
    { id: 6, text: "Macroeconomic US Stocks Data" },
    { id: 7, text: "Analyst Estimates" },
    { id: 8, text: "Stock Prices (OHLC)" },
    { id: 9, text: "Super Investor Holdings" },
    { id: 10, text: "Insider Trading" },
    { id: 11, text: "Earnings Calendar" },
  ];
  const allPlans = useSelector((state: any) => state.plans.plans);
  const plan = useSelector((state: any) => state.plan.plan);
  const term = useSelector((state: any) => state?.plan?.plan?.subscription?.billing_period || "month") + "ly";
  const plans = useMemo(() => {
    const freePlan = [
      {
        id: "",
        planName: "Free",
        price: "$0",
        billingCycle: "Forever",
        prompts: 15,
        buttonText: "Manage My Subscription",
        buttonType: "primary",
        numPrompts: 15,
      },
    ];
    const currPlans = allPlans[term]
    if (currPlans?.length) {
      const paid = currPlans.map((p: any) => ({
        planName: p.metadata.label,
        price: `$${Number(p.price / 100)?.toLocaleString() || ""}`,
        billingCycle: "Billed monthly, ex. tax",
        prompts:
          p.metadata.prompts === 999999 ? "Unlimited" : p.metadata.prompts,
        buttonText: `Upgrade to ${p.metadata.label}`,
        buttonType: "primary",
        id: p.id,
        numPrompts: p.metadata.prompts,
      }));
      const all = freePlan.concat(paid);
      return all
        .sort((a: any, b: any) => a.numPrompts - b.numPrompts)
        .map((item: any) => {
          const currentPlan = plan.subscription_type === item.planName;
          const buttonText = currentPlan
          ? "Manage my Subscription"
          : `Upgrade to ${item.planName}`;
          return {
            ...item,
            currentPlan,
            buttonText,
          };
        });
      }
      return [];
    }, [allPlans, plan]);

  const user = useSelector((state: RootState) => state.auth.currentUser);
  const currentPlan = plans.find((a) => a.currentPlan);
  const nonRenewing = useMemo(() => {
    return !!plan?.non_renewing
  }, [plan]);
  const exp = useMemo(() => {
    if (!user || !nonRenewing) return false;
    return moment(plan?.subscription?.expires_at).format('DD MMM YYYY');
  }, [plan])
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={style}
        className="ModalMyPlanFreeTier bg-white rounded-[10px] shadow flex-col justify-center items-start inline-flex"
      >
        <div className="Header self-stretch bg-neutral-100  justify-start items-center inline-flex">
          <div className="Info p-[24px] h-[73px]  grow shrink basis-0 self-stretch justify-between items-center flex">
            <div className="LeftInfo justify-start items-center flex">
              <div className="Text text-zinc-900 text-xl font-semibold font-['Inter'] leading-[25px]">
                Your Current Plan
              </div>
            </div>
            <div className="RightInfo justify-center items-center flex">
              <div className="Icons w-5 h-5 relative">
                <IconButton
                  onClick={handleClose}
                  className="w-2.5 h-2.5 left-[5px] top-[-4px] absolute"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`Content self-stretch h-[630px] px-6 pt-4 pb-6 flex-col justify-start items-start gap-6 inline-flex`}
        >
          <div className="TierTitle self-stretch h-[75px] flex-col justify-center items-start flex">
            <div className="Title w-[368px] justify-start items-start inline-flex">
              <div
                className={`${textColorClass} Text grow shrink basis-0 h-7 plan-text text-xl font-bold font-['Inter'] leading-[25px]`}
              >
                {currentPlan?.planName || ""}
              </div>
            </div>
            <div className="Subtitle self-stretch justify-start items-start inline-flex">
              <div className="Usd0Month text-neutral-400 text-base font-normal font-['Inter'] leading-snug">
                USD {currentPlan?.price || ""}/{term?.replace('ly','')}
              </div>
            </div>
            <div className="PromptsPerMonth self-stretch justify-start items-start gap-1 inline-flex">
              <div
                className={`${textColorClass} PromptsPerMonth grow shrink basis-0 plan-text text-xl font-bold font-['Inter'] leading-[25px]`}
              >
                {currentPlan?.prompts === 999
                  ? "Unlimited"
                  : currentPlan?.prompts || ""}{" "}
                prompts per day
              </div>

            </div>
          </div>
          {
            nonRenewing && (
              <div className={` ${textColorClass} flex font-inter text-xs font-semibold leading-4 gap-[10px] pl-[1px]`}>
                <img src={Alert} className=" pt-[1px] w-[16px] h-[16px]" />
                Your subscription has been cancelled and will expire on {exp}. After which, your plan will be downgraded to the Free package.
              </div>
            )
          }
          <div className="Cta self-stretch h-[50px] flex-col justify-start items-start flex">
            <Button
              onClick={() => navigate("/plans")}
              classes={{
                root: "!text-white Cta !self-stretch h-[50px] !px-4 !py-[5px] !bg-zinc-900 !rounded-[10px] !justify-center !items-center !gap-2.5 !inline-flex !normal-case",
              }}
            >
              {currentPlan?.planName !== "Premium"
                ? "Upgrade Now"
                : "Manage My Plan"}
            </Button>
          </div>
          <div className="Usp self-stretch h-[324px] flex-col justify-start items-start gap-2 flex">
            <div className="Subheader justify-start items-start gap-1 inline-flex">
              <div className="AllPlansInclude text-neutral-400 text-xs font-normal font-['Inter'] leading-none">
                All plans include:
              </div>
            </div>
            {points.map((point: Point) => (
              <div
                key={point.id}
                className="Point self-stretch justify-start items-center gap-1 inline-flex"
              >
                <div className="CheckFill w-5 h-5 relative">
                  <img
                    alt="checkbox"
                    className="Subtract w-[20px] h-[20px] left-[1.50px] top-[1.50px] absolute"
                    src={CheckFill}
                  />
                </div>
                <div
                  className={`${textColorClass} text-sm font-normal font-['Inter'] leading-[18px] plan-text`}
                >
                  {point.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default PlanModal;
